import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useIsAccessTokenValidQuery,
  useReportDefinitionNewForSurveyLazyQuery,
  useReportDefinitionNewForTopicLazyQuery,
  useSaveReportDefinitionNewForSurveyMutation,
  useSaveReportDefinitionNewForTopicMutation,
  useSetPrimaryReportDefinitionNewForSurveyMutation,
  useSetPrimaryReportDefinitionNewForTopicMutation,
  UserOrgRole,
  UserSysRole,
  ReportDefinitionsNewForSurveyDocument,
  ReportDefinitionsNewForTopicDocument,
  type ReportDefinitionsNewForSurveyQuery,
  type ReportDefinitionsNewForTopicQuery,
} from '../../../generated/hooks';
import type { NewReportData } from '../types';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { generateDefaultDefinition } from './generateDefaultDefinition';
import { parseReportDefinition, validateDefinitionQuestions } from './validation';
import { createMetricErrorCache, getMetricErrors, getMetricIdentifierErrors } from '@arnold/core';
import type { ReportDefinitionDocument, ReportMetricCalculation } from './types';

// parseReportDefinition/dirty-checking walk and JSON-serialize the whole document (up to 200 metrics, 500
// components); trailing them by this much avoids redoing that on every keystroke while still typing.
const VALIDATION_DEBOUNCE_MS = 300;

const EMPTY: ReportDefinitionDocument = { schemaVersion: 1, metrics: [], pages: { overview: { components: [] } } };
const NO_METRICS: ReportMetricCalculation[] = [];
const NO_QUESTIONS: NewReportData['questions'] = [];
type DraftState = {
  scope: string;
  open: boolean;
  draft?: ReportDefinitionDocument;
  savedId?: string;
  savedJson?: string;
  preview?: ReportDefinitionDocument;
  previewId?: string;
};

export type ReportDefinitionController = ReturnType<typeof useReportDefinition>;

export function useReportDefinition(report: NewReportData | undefined, scope: string, basePath?: string) {
  const { t } = useTranslation('newReport');
  const history = useHistory();
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const auth = useIsAccessTokenValidQuery();
  const user = auth.data?.isAccessTokenValid.user;
  const canEdit =
    user?.systemRole === UserSysRole.SysAdmin ||
    !!user?.respondents?.some(
      (respondent) =>
        respondent.organizationRole === UserOrgRole.OrgAdmin &&
        respondent.organization.id === report?.metadata.organization.id,
    );
  const [stored, setStored] = useState<DraftState>({ scope, open: false });
  const state = stored.scope === scope ? stored : { scope, open: false };
  const [operationError, setOperationError] = useState<string>();
  useEffect(() => setOperationError(undefined), [scope]);
  const [saveForSurveyMutation, savingForSurvey] = useSaveReportDefinitionNewForSurveyMutation();
  const [saveForTopicMutation, savingForTopic] = useSaveReportDefinitionNewForTopicMutation();
  const [setPrimaryForSurveyMutation, settingPrimaryForSurvey] = useSetPrimaryReportDefinitionNewForSurveyMutation();
  const [setPrimaryForTopicMutation, settingPrimaryForTopic] = useSetPrimaryReportDefinitionNewForTopicMutation();
  const [loadForSurvey, previewingForSurvey] = useReportDefinitionNewForSurveyLazyQuery({
    fetchPolicy: 'network-only',
  });
  const [loadForTopic, previewingForTopic] = useReportDefinitionNewForTopicLazyQuery({ fetchPolicy: 'network-only' });
  const base = useMemo(() => {
    if (!report) return { document: EMPTY };
    if (!report.reportDefinition) return { document: generateDefaultDefinition(report) };
    try {
      return { document: parseReportDefinition(report.reportDefinition.definition) };
    } catch (error) {
      return { document: EMPTY, error: (error as Error).message };
    }
  }, [report]);
  const open = canEdit && state.open;
  const document = open ? state.preview ?? state.draft ?? base.document : base.document;
  const documentError = document === base.document ? base.error : undefined;
  const debouncedDocument = useDebouncedValue(document, VALIDATION_DEBOUNCE_MS);
  const validationError = useMemo(() => {
    try {
      parseReportDefinition(debouncedDocument);
      if (report) validateDefinitionQuestions(debouncedDocument, report.questions);
      return undefined;
    } catch (error) {
      return (error as Error).message;
    }
  }, [debouncedDocument, report]);
  const metricErrorCache = useRef(createMetricErrorCache());
  const calculationErrors = useMemo(
    () => getMetricErrors(debouncedDocument.metrics, report?.questions ?? NO_QUESTIONS, metricErrorCache.current),
    [debouncedDocument.metrics, report?.questions],
  );
  const metricErrors = useMemo(
    () => ({ ...getMetricIdentifierErrors(debouncedDocument.metrics), ...calculationErrors }),
    [debouncedDocument.metrics, calculationErrors],
  );
  // Titles/layout edits don't rebuild cohorts, and an unfinished metric doesn't blank all existing results.
  const calculationKey = JSON.stringify(
    debouncedDocument.metrics
      .filter((metric) => !calculationErrors[metric.id])
      .map(({ id, calculation, presentation }) => ({ id, calculation, presentation })),
  );
  const calculations = useMemo(() => JSON.parse(calculationKey) as ReportMetricCalculation[], [calculationKey]);
  const debouncedDraft = useDebouncedValue(state.draft, VALIDATION_DEBOUNCE_MS);
  const dirty = useMemo(
    () =>
      !!state.draft &&
      JSON.stringify(debouncedDraft ?? state.draft) !== (state.savedJson ?? JSON.stringify(base.document)),
    [state.draft, debouncedDraft, state.savedJson, base.document],
  );
  useEffect(() => {
    if (!dirty) return;
    const unload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', unload);
    // Moving between the report's own pages keeps the draft; only leaving the report needs a confirmation.
    const base = basePath?.replace(/\/+$/, '').toLowerCase();
    const unblock = history.block((location) => {
      const target = location.pathname.toLowerCase();
      if (base && (target === base || target.startsWith(`${base}/`))) return;
      return String(t('definition.discardConfirm'));
    });
    return () => {
      unblock();
      window.removeEventListener('beforeunload', unload);
    };
  }, [dirty, history, t, basePath]);
  const patch = (update: Partial<DraftState>) =>
    setStored((previous) => ({ ...(previous.scope === scope ? previous : { scope, open: false }), ...update }));
  const edit = (update: (value: ReportDefinitionDocument) => ReportDefinitionDocument) => {
    setStored((previous) => {
      const current = previous.scope === scope ? previous : { scope, open: true };
      return {
        ...current,
        draft: update(current.draft ?? base.document),
        preview: undefined,
        previewId: undefined,
        open: true,
      };
    });
  };
  const loadDefinition = async (id: string) => {
    if (!report) throw new Error('preview');
    try {
      const surveyVersion = await loadForSurvey({ variables: { surveyId: report.surveyId, id } });
      if (surveyVersion.data) return surveyVersion.data.reportDefinitionNewForSurvey.definition;
    } catch {
      // A topic-only version is not linked to this survey.
    }
    const topicVersion = await loadForTopic({ variables: { surveyId: report.surveyId, id } });
    if (!topicVersion.data) throw new Error('preview');
    return topicVersion.data.reportDefinitionNewForTopic.definition;
  };
  const preview = async (id: string) => {
    if (!report || !canEdit) return;
    setOperationError(undefined);
    try {
      const definition = parseReportDefinition(await loadDefinition(id));
      if (currentScope.current !== scope) return;
      patch({ preview: definition, previewId: id });
    } catch {
      setOperationError(t('definition.operationFailed'));
    }
  };
  const saveForSurvey = async (name: string): Promise<string | undefined> => {
    if (!report || !canEdit || documentError || state.preview) return undefined;
    // `validationError` trails the draft by VALIDATION_DEBOUNCE_MS, so check the live document here rather than
    // trusting a stale pass.
    try {
      parseReportDefinition(document);
      validateDefinitionQuestions(document, report.questions);
    } catch {
      return undefined;
    }
    setOperationError(undefined);
    const captured = JSON.stringify(document);
    try {
      const result = await saveForSurveyMutation({
        variables: { surveyId: report.surveyId, name: name.trim() || null, definition: document },
        update: (cache, result) => {
          const version = result.data?.saveReportDefinitionNewForSurvey;
          const query = { query: ReportDefinitionsNewForSurveyDocument, variables: { surveyId: report.surveyId } };
          const cached = cache.readQuery<ReportDefinitionsNewForSurveyQuery>(query);
          if (version && cached)
            cache.writeQuery({
              ...query,
              data: {
                reportDefinitionsNewForSurvey: [
                  version,
                  ...cached.reportDefinitionsNewForSurvey.filter((item) => item.id !== version.id),
                ],
              },
            });
        },
      });
      if (!result.data) throw new Error('save');
      const savedId = result.data.saveReportDefinitionNewForSurvey.id;
      setStored((previous) =>
        previous.scope === scope
          ? { ...previous, draft: previous.draft ?? document, savedJson: captured, savedId }
          : previous,
      );
      return savedId;
    } catch {
      setOperationError(t('definition.operationFailed'));
      return undefined;
    }
  };
  const saveForTopic = async (name: string, primary: boolean): Promise<string | undefined> => {
    if (!report || !canEdit || validationError || documentError || state.preview) return undefined;
    setOperationError(undefined);
    const captured = JSON.stringify(document);
    try {
      const result = await saveForTopicMutation({
        variables: { surveyId: report.surveyId, name: name.trim() || null, definition: document, primary },
        refetchQueries: primary ? ['GetNewReport', 'ReportDefinitionsNewForTopic'] : ['ReportDefinitionsNewForTopic'],
        awaitRefetchQueries: primary,
      });
      if (!result.data) throw new Error('save');
      const savedId = result.data.saveReportDefinitionNewForTopic.id;
      setStored((previous) =>
        previous.scope === scope
          ? { ...previous, draft: previous.draft ?? document, savedJson: captured, savedId }
          : previous,
      );
      return savedId;
    } catch {
      setOperationError(t('definition.operationFailed'));
      return undefined;
    }
  };
  const markPrimary = <Version extends { id: string }>(versions: Version[], id: string) =>
    versions.map((version) => ({ ...version, isPrimary: version.id === id }));
  const setPrimaryFor = async (id: string, owner: 'survey' | 'topic') => {
    if (!report || !canEdit) return;
    setOperationError(undefined);
    try {
      const definition = parseReportDefinition(await loadDefinition(id));
      if (currentScope.current !== scope) return;
      validateDefinitionQuestions(definition, report.questions);
      const historyQueries = ['ReportDefinitionsNewForSurvey', 'ReportDefinitionsNewForTopic'];
      await (owner === 'survey' ? setPrimaryForSurveyMutation : setPrimaryForTopicMutation)({
        variables: { surveyId: report.surveyId, id },
        refetchQueries: ['GetNewReport', ...historyQueries],
        update: (cache) => {
          const surveyQuery = {
            query: ReportDefinitionsNewForSurveyDocument,
            variables: { surveyId: report.surveyId },
          };
          const topicQuery = {
            query: ReportDefinitionsNewForTopicDocument,
            variables: { surveyId: report.surveyId },
          };
          const surveyCache = cache.readQuery<ReportDefinitionsNewForSurveyQuery>(surveyQuery);
          const topicCache = cache.readQuery<ReportDefinitionsNewForTopicQuery>(topicQuery);
          if (owner === 'survey' && surveyCache)
            cache.writeQuery({
              ...surveyQuery,
              data: { reportDefinitionsNewForSurvey: markPrimary(surveyCache.reportDefinitionsNewForSurvey, id) },
            });
          if (owner === 'topic' && topicCache)
            cache.writeQuery({
              ...topicQuery,
              data: { reportDefinitionsNewForTopic: markPrimary(topicCache.reportDefinitionsNewForTopic, id) },
            });
        },
        awaitRefetchQueries: true,
      });
    } catch {
      setOperationError(t('definition.operationFailed'));
    }
  };
  return {
    document,
    metrics: documentError ? NO_METRICS : calculations,
    metricErrors,
    canEdit,
    open,
    dirty,
    savedId: state.savedId,
    previewId: state.previewId,
    error: operationError,
    validationError: documentError ?? validationError,
    busy:
      savingForSurvey.loading ||
      savingForTopic.loading ||
      settingPrimaryForSurvey.loading ||
      settingPrimaryForTopic.loading ||
      previewingForSurvey.loading ||
      previewingForTopic.loading,
    primaryId: report?.reportDefinition?.id,
    toggle: () => patch({ open: !state.open }),
    close: () => patch({ open: false }),
    edit,
    restore: (value: ReportDefinitionDocument) => edit(() => value),
    saveForSurvey,
    saveForTopic,
    setPrimaryForSurvey: (id: string) => setPrimaryFor(id, 'survey'),
    setPrimaryForTopic: (id: string) => setPrimaryFor(id, 'topic'),
    preview,
    exitPreview: () => patch({ preview: undefined, previewId: undefined }),
    editPreview: () => {
      if (!state.preview || (dirty && !window.confirm(t('definition.discardConfirm')))) return;
      patch({
        draft: state.preview,
        savedId: state.previewId,
        savedJson: JSON.stringify(state.preview),
        preview: undefined,
        previewId: undefined,
      });
    },
    discard: () => {
      if (!dirty || window.confirm(t('definition.discardConfirm')))
        patch({
          open: false,
          draft: undefined,
          savedId: undefined,
          savedJson: undefined,
          preview: undefined,
          previewId: undefined,
        });
    },
  };
}
